import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentSection } from '@/features/scheduler/components/ContentSection/ContentSection';
import type { CarouselMediaItem } from '@/features/scheduler/components/ContentSection/CarouselContent';
import { CAROUSEL_MAX_ITEMS, CAROUSEL_MIN_ITEMS, inferFeedPublicationFormat } from '@/features/scheduler/utils/carouselMedia';
import { validateReelsMedia } from '@/features/scheduler/utils/mediaValidation';
import { ProfileSection } from '@/features/scheduler/components/ProfileSection/ProfileSection';
import { ScheduleSection } from '@/features/scheduler/components/ScheduleSection/ScheduleSection';
import scheduledPostService, { CarouselItemProgress, CarouselParentProgress, CarouselScheduleError, SaveScheduledResponse } from '@/features/scheduler/services/SaveScheduledPost';
import './ScheduleContent.scss';
import '@/features/scheduler/pages/ContentScheduler.scss';
import { usePages } from '@/core/contexts/PagesContext';
import { Page } from '@/core/services/PageService';
import { useNavigate } from 'react-router-dom';
import { Paths } from '@/routes/paths/Paths';

const getReelsCoverOptions = (feedFormat: string | null, coverFile: File | null) => {
  if (feedFormat !== 'reels' || !coverFile) return undefined;
  return { cover: coverFile };
};

const isCarouselScheduleError = (error: unknown): error is CarouselScheduleError =>
  error instanceof Error &&
  error.name === 'CarouselScheduleError' &&
  'outcomeUnknown' in error &&
  typeof error.outcomeUnknown === 'boolean';

const formatScheduleDate = (date: Date): string =>
  `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;

const getCurrentScheduleDate = (): string => formatScheduleDate(new Date());

export const ScheduleContent: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [account, setAccount] = useState<string | undefined>(undefined);
  const [publicationType, setPublicationType] = useState<string>('');
  const [reelsShareToFeed, setReelsShareToFeed] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [carouselItems, setCarouselItems] = useState<CarouselMediaItem[]>([]);
  const [carouselOutcomeUnknown, setCarouselOutcomeUnknown] = useState(false);
  const [carouselNeedsRetry, setCarouselNeedsRetry] = useState(false);
  const [carouselParent, setCarouselParent] = useState<CarouselParentProgress | null>(null);
  const [contentData, setContentData] = useState<{
    file: File | null;
    isValid: boolean;
    validation: any;
    tags: string[];
    caption: string;
    reelsCoverFile: File | null;
  }>({
    file: null,
    isValid: false,
    validation: null,
    tags: [],
    caption: '',
    reelsCoverFile: null
  });

  const [scheduleData, setScheduleData] = useState<{
    type: 'now' | 'scheduled';
    date: string;
    time: string;
  }>(() => ({
    type: 'scheduled',
    date: getCurrentScheduleDate(),
    time: ''
  }));

  const { pages, isLoading } = usePages();
  const feedFormat = inferFeedPublicationFormat(carouselItems);

  const resetForm = () => {
    setContentData({
      file: null,
      isValid: false,
      validation: null,
      tags: [],
      caption: '',
      reelsCoverFile: null
    });
    setCarouselItems([]);
    setCarouselOutcomeUnknown(false);
    setCarouselNeedsRetry(false);
    setCarouselParent(null);
    setScheduleData({
      type: 'scheduled',
      date: getCurrentScheduleDate(),
      time: ''
    });
    setPublicationType('');
    setReelsShareToFeed(true);
    setAccount(undefined);
  };

  const validateCarouselSelection = async (): Promise<boolean> => {
    if (carouselItems.length < CAROUSEL_MIN_ITEMS || carouselItems.length > CAROUSEL_MAX_ITEMS) {
      await showToast('error', t('carousel.invalidCountTitle'), t('carousel.invalidCount'));
      return false;
    }
    if (carouselItems.some((item) => item.status === 'error' || Boolean(item.error))) {
      await showToast('error', t('carousel.imageErrorTitle'), t('carousel.imageError'));
      return false;
    }
    if (carouselItems.some((item) => item.status !== 'ready')) {
      await showToast('error', t('carousel.processingTitle'), t('carousel.processingMessage'));
      return false;
    }
    return true;
  };

  const validateStoriesSelection = async (): Promise<boolean> => {
    if (!contentData.file) {
      await showToast('error', t('errors.fileNotSelectedTitle'), t('errors.fileNotSelected'));
      return false;
    }
    if (!contentData.isValid) {
      await showToast('error', t('errors.invalidFileTitle'), t('errors.invalidFile'));
      return false;
    }
    return true;
  };

  const validateReelsSelection = async (): Promise<boolean> => {
    if (!feedFormat || carouselItems[0]?.status !== 'ready' || carouselItems[0]?.error) {
      await showToast('error', t('errors.fileNotSelectedTitle'), t('errors.fileNotSelected'));
      return false;
    }
    if (feedFormat !== 'reels') return true;
    if (contentData.file !== carouselItems[0].file) {
      const validation = await validateReelsMedia(carouselItems[0].file, t);
      if (!validation.isValid) {
        await showToast('error', t('errors.invalidFileTitle'), validation.errors.join(' '));
        return false;
      }
      return true;
    }
    if (!contentData.isValid) {
      await showToast('error', t('errors.invalidFileTitle'), t('errors.invalidFile'));
      return false;
    }
    return true;
  };

  const validateContentSelection = async (): Promise<boolean> => {
    if (publicationType === 'stories') return validateStoriesSelection();
    if (feedFormat === 'carousel') return validateCarouselSelection();
    return validateReelsSelection();
  };

  const validateScheduling = async (): Promise<boolean> => {
    if (carouselOutcomeUnknown) {
      await showToast('error', t('carousel.outcomeUnknownTitle'), t('carousel.outcomeUnknownMessage'));
      return false;
    }
    if (!account) {
      await showToast('error', t('errors.accountNotSelectedTitle'), t('errors.accountNotSelected'));
      return false;
    }
    if (!publicationType) {
      await showToast('error', t('errors.notSelectedPostType'), t('errors.selectPostType'));
      return false;
    }
    if (!(await validateContentSelection())) return false;
    return validateDateTime();
  };

  const validateDateTime = async (): Promise<boolean> => {
    if (scheduleData.type !== 'scheduled') return true;

    if (!scheduleData.date || !scheduleData.time) {
      await showToast('error', t('errors.dateTimeNotProvidedTitle'), t('errors.dateTimeNotProvided'));
      return false;
    }

    const [day, month, year] = scheduleData.date.split('/');
    const [hours, minutes] = scheduleData.time.split(':');
    const scheduledDateTime = new Date(
      Number.parseInt(year),
      Number.parseInt(month) - 1,
      Number.parseInt(day),
      Number.parseInt(hours),
      Number.parseInt(minutes)
    );

    if (scheduledDateTime < new Date()) {
      await showToast('error', t('errors.invalidDateTimeTitle'), t('errors.invalidDateTime'));
      return false;
    }
    return true;
  };

  // Função para capturar mudanças na validação do arquivo
  const handleValidationChange = (isValid: boolean, file: File | null, validation: any) => {
    setContentData(prev => ({
      ...prev,
      file,
      isValid,
      validation
    }));
  };

  const handleCarouselItemsChange = (items: CarouselMediaItem[]) => {
    setCarouselItems(items);
    if (inferFeedPublicationFormat(items) !== 'reels') {
      setContentData((current) => ({ ...current, file: null, isValid: false, validation: null }));
    }
    setCarouselParent(null);
    setCarouselNeedsRetry(false);
    setCarouselOutcomeUnknown(false);
  };

  // Função para capturar mudanças nas tags (Stories)
  const handleTagsChange = (tags: string[]) => {
    setContentData(prev => ({
      ...prev,
      tags
    }));
  };

  // Função para capturar mudanças na legenda (Feed e Reels)
  const handleCaptionChange = (caption: string) => {
    setContentData(prev => ({
      ...prev,
      caption
    }));
  };

  const handleReelsCoverFileChange = (reelsCoverFile: File | null) => {
    setContentData(prev => ({
      ...prev,
      reelsCoverFile
    }));
  };


  const handleAccountChange = (nextAccount: string | undefined) => {
    if (account !== nextAccount) {
      if (carouselItems.length > 1) {
        setCarouselItems([]);
      } else if (carouselItems.length === 1) {
        setCarouselItems((current) => current.map((item) => {
          const hasAccountBoundContainer = Boolean(item.containerId || item.containerPayload || item.retryStatus);
          return {
            ...item,
            status: hasAccountBoundContainer ? 'ready' : item.status,
            error: item.retryStatus ? undefined : item.error,
            containerId: undefined,
            containerPayload: undefined,
            containerCreatedAt: undefined,
            retryStatus: undefined,
            retryError: undefined,
          };
        }));
      }
      setCarouselParent(null);
      setCarouselNeedsRetry(false);
      setCarouselOutcomeUnknown(false);
    }
    setAccount(nextAccount);
  };

  const handlePublicationTypeChange = (type: string) => {
    if (type !== publicationType) {
      setCarouselItems([]);
      setCarouselParent(null);
      setCarouselNeedsRetry(false);
      setCarouselOutcomeUnknown(false);
      setContentData((current) => ({
        ...current,
        file: null,
        isValid: false,
        validation: null,
        caption: '',
        tags: [],
        reelsCoverFile: null,
      }));
      setReelsShareToFeed(true);
    }
    setPublicationType(type);
  };

  // Função para capturar mudanças no tipo de agendamento
  const handleScheduleTypeChange = (type: 'now' | 'scheduled') => {
    setScheduleData(prev => ({
      ...prev,
      type
    }));
  };

  // Função para capturar mudanças na data e hora do agendamento
  const handleDateTimeChange = (date: string | Date, time: string) => {    
    let formattedDate = '';
    const formattedTime = time || '';
    
    // Check if date is a Date object
    if (date instanceof Date) {
      formattedDate = formatScheduleDate(date);
    } else if (typeof date === 'string') {
      // If date is string and contains 'T', it's ISO format
      if (date.includes('T')) {
        formattedDate = formatScheduleDate(new Date(date));

      } else {
        // Already in DD/MM/YYYY format
        formattedDate = date;
      }
    }
    
    setScheduleData(prev => ({
      ...prev,
      date: formattedDate,
      time: formattedTime
    }));
  };

  const showToast = async (variant: 'success' | 'error', title: string, text: string) => {
    const toast = document.createElement('bds-toast');
    document.body.appendChild(toast);

    await toast.create({
      variant,
      actionType: 'icon',
      toastTitle: title,
      buttonAction: 'close',
      toastText: text,
      icon: variant === 'success' ? 'like' : 'error',
      duration: 4,
    });
  };

  const getScheduleButtonLabel = (): string => {
    if (isUploading) return t('scheduling');
    if (carouselNeedsRetry) return t('carousel.retry');
    return t('schedule');
  };

  const getMediaTypeForAPI = (): 'STORIES' | 'REELS' | 'IMAGE' => {
    if (publicationType === 'stories') return 'STORIES';
    if (feedFormat === 'reels') return 'REELS';
    if (feedFormat === 'image') return 'IMAGE';
    throw new Error(t('errors.invalidPublicationType'));
  };

  const updateCarouselItemProgress = (itemId: string, progress: CarouselItemProgress) => {
    setCarouselItems((current) => current.map((item) => (
      item.id === itemId
        ? {
          ...item,
          status: progress.status,
          retryStatus: progress.retryStatus,
          retryError: progress.message,
          error: progress.status === 'error' ? progress.message : item.error,
          containerId: progress.containerId,
          containerPayload: progress.containerPayload,
          containerCreatedAt: progress.containerCreatedAt,
        }
        : item
    )));
  };

  const scheduleCarousel = async (pageId: string): Promise<SaveScheduledResponse> => {
    const response = await scheduledPostService.scheduleCarouselPost({
      fbIgId: pageId,
      items: carouselItems.map((item) => ({
        id: item.id,
        name: item.sourceName,
        file: item.file,
        fileType: item.fileType,
        isAiGenerated: item.isAiGenerated === true,
        containerId: item.containerId,
        containerPayload: item.containerPayload,
        containerCreatedAt: item.containerCreatedAt,
        retryStatus: item.retryStatus,
      })),
      parentContainerId: carouselParent?.containerId,
      parentPayload: carouselParent?.payload,
      onParentProgress: setCarouselParent,
      onItemProgress: updateCarouselItemProgress,
      scheduleType: scheduleData.type,
      caption: contentData.caption || undefined,
      scheduledDate: scheduleData.type === 'scheduled' ? scheduleData.date : undefined,
      scheduledTime: scheduleData.time || undefined,
      isAiGenerated: carouselItems.some((item) => item.isAiGenerated === true),
    });
    if (response.success === false) {
      throw new Error(response.message || response.error || t('errors.genericError'));
    }
    return response;
  };

  const scheduleSingleMedia = async (pageId: string): Promise<SaveScheduledResponse> =>
    scheduledPostService.schedulePost(
      pageId,
      publicationType === 'stories' ? contentData.file! : carouselItems[0].file,
      getMediaTypeForAPI(),
      scheduleData.type,
      contentData.caption || undefined,
      scheduleData.type === 'scheduled' ? scheduleData.date : undefined,
      scheduleData.time || undefined,
      contentData.tags.length > 0 ? contentData.tags : undefined,
      {
        reelsCover: getReelsCoverOptions(feedFormat, contentData.reelsCoverFile),
        shareToFeed: reelsShareToFeed,
      }
    );

  const showScheduleSuccessToast = async (response: SaveScheduledResponse | undefined) => {
    const isPending = feedFormat === 'carousel' && ['PENDING', 'PROCESSING', 'RETRYING'].includes(
      String(response?.status ?? '').toUpperCase()
    );
    const toastPrefix = publicationType === 'stories' ? 'story' : 'post';
    const toastMode = scheduleData.type === 'now' ? 'Now' : 'Scheduled';
    const successTitle = t(`toast.${toastPrefix}${toastMode}Success`);
    const scheduleMessage = isPending
      ? t('carousel.pendingMessage')
      : t(`toast.${toastPrefix}${toastMode}Message`, {
        date: scheduleData.date,
        time: scheduleData.time,
      });

    await showToast('success', isPending ? t('carousel.pendingTitle') : successTitle, scheduleMessage);
  };

  const handleScheduleError = async (error: any) => {
    const carouselError = isCarouselScheduleError(error) ? error : null;
    if (carouselError && !carouselError.outcomeUnknown && feedFormat === 'carousel') {
      setCarouselNeedsRetry(true);
    }
    if (carouselError?.outcomeUnknown) {
      setCarouselOutcomeUnknown(true);
      await showToast('error', t('carousel.outcomeUnknownTitle'), t('carousel.outcomeUnknownMessage'));
      return;
    }
    if (carouselError?.stillProcessing) {
      // Instagram hasn't finished processing one or more containers yet (common for
      // larger videos). Show a reassuring message instead of the raw backend text.
      await showToast('error', t('carousel.containersProcessingTitle'), t('carousel.containersProcessingMessage'));
      return;
    }
    const errorMessage = error?.response?.data?.message || error?.message || t('errors.genericError');
    await showToast('error', t('toast.scheduleError'), errorMessage);
  };

  const handleScheduleClick = async () => {
    const isFormValid = await validateScheduling();
    if (!isFormValid) return;

    setIsUploading(true);
    try {
      const selectedPage = pages.find(p => p.id === account);
      if (!selectedPage?.id) throw new Error(t('errors.pageNotFound'));

      const response = feedFormat === 'carousel'
        ? await scheduleCarousel(selectedPage.id)
        : await scheduleSingleMedia(selectedPage.id);

      await showScheduleSuccessToast(response);
      resetForm();
    } catch (error: any) {
      await handleScheduleError(error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div className="header">
        <bds-typo variant="fs-24" bold="bold">
          {t('createNewPublication')}
        </bds-typo>
      </div>
      <bds-paper class="scheduler-container" elevation="none" width="auto">
        <div className="container-header">
          <bds-typo variant="fs-20" bold="bold">
            {t('fillInformation')}
          </bds-typo>
        </div>
        <div className="scheduler-container-content">
          <ProfileSection
            account={account}
            setAccount={handleAccountChange}
            pages={pages.map((page: Page) => ({
              value: page.id,
              label: `${page.title} (${page.username})`,
            }))}
            loading={isLoading}
            disabled={isUploading || carouselOutcomeUnknown || Boolean(carouselParent)}
          />
          <ContentSection
            publicationType={publicationType}
            setPublicationType={handlePublicationTypeChange}
            onValidationChange={handleValidationChange}
            onTagsChange={handleTagsChange}
            onCaptionChange={handleCaptionChange}
            caption={contentData.caption}
            reelsCoverFile={contentData.reelsCoverFile}
            onReelsCoverFileChange={handleReelsCoverFileChange}
            shareToFeed={reelsShareToFeed}
            onShareToFeedChange={setReelsShareToFeed}
            carouselItems={carouselItems}
            onCarouselItemsChange={handleCarouselItemsChange}
            onCarouselOverflow={() => showToast('error', t('carousel.tooManyItemsTitle'), t('carousel.tooManyItemsMessage'))}
            disabled={isUploading || carouselOutcomeUnknown || Boolean(carouselParent)}
          />
          <ScheduleSection 
            selectedDate={scheduleData.date}
            onScheduleTypeChange={handleScheduleTypeChange}
            onDateTimeChange={handleDateTimeChange}
          />
        </div>
        <div className="scheduler-container-actions">
          <bds-button class="scheduler-cancel-button" variant="secondary" disabled={isUploading} onClick={() => navigate(Paths.listScheduledContent)}>
            {t('cancel')}
          </bds-button>
          <bds-button 
            variant="primary" 
            onClick={handleScheduleClick}
            disabled={isUploading || carouselOutcomeUnknown}
          >
            {getScheduleButtonLabel()}
          </bds-button>
        </div>
      </bds-paper>
    </>
  );
};
